/* eslint-disable no-console */
/**
 * useImageCallbacks Hook
 *
 * Manages callback registration for image-related actions.
 * Handles both recommendation flow callbacks and direct tool use callbacks.
 *
 * Unix Philosophy: One hook does one thing well - callback management.
 */

import { useRef, useCallback } from 'react';

import type { SolomonRecommendation, ImageGenerationResult } from '../../components/global/SolomonPanel/types';

/** Callback for recommendation-based image generation */
type ActionCallback = (rec: SolomonRecommendation, result: ImageGenerationResult) => void;

/** Callback for direct tool use image applied (SSE streaming flow) */
type ImageAppliedCallback = (imageKey: string, imageUrl: string, previousUrl: string | null) => void;

/** Callback for saving images directly (bypasses unsavedAIImages state) */
type SaveImageDirectCallback = (imageKey: string, imageUrl: string) => Promise<boolean>;

interface UseImageCallbacksReturn {
  /** Current action callback ref (for recommendation flow) */
  actionCallbackRef: React.MutableRefObject<ActionCallback | null>;
  /** Current image applied callback ref (for direct tool use flow) */
  imageAppliedCallbackRef: React.MutableRefObject<ImageAppliedCallback | null>;
  /** Current save image direct callback ref */
  saveImageDirectCallbackRef: React.MutableRefObject<SaveImageDirectCallback | null>;
  /** Register callback for recommendation-based actions */
  registerActionCallback: (callback: ActionCallback) => void;
  /** Unregister action callback */
  unregisterActionCallback: () => void;
  /** Register callback for direct tool use image applied */
  registerImageAppliedCallback: (callback: ImageAppliedCallback) => void;
  /** Unregister image applied callback */
  unregisterImageAppliedCallback: () => void;
  /** Notify when image is applied via direct tool use */
  notifyImageApplied: (imageKey: string, imageUrl: string, previousUrl: string | null) => void;
  /** Invoke action callback if registered */
  invokeActionCallback: (rec: SolomonRecommendation, result: ImageGenerationResult) => void;
  /** Register callback for saving images directly */
  registerSaveImageDirectCallback: (callback: SaveImageDirectCallback) => void;
  /** Unregister save image direct callback */
  unregisterSaveImageDirectCallback: () => void;
  /** Save image directly (bypasses state, takes imageKey and imageUrl) */
  saveImageDirect: (imageKey: string, imageUrl: string) => Promise<boolean>;
}

export function useImageCallbacks(): UseImageCallbacksReturn {
  // Callback ref for action applied (recommendation flow)
  const actionCallbackRef = useRef<ActionCallback | null>(null);

  // Callback ref for direct tool use image applied (SSE streaming flow)
  const imageAppliedCallbackRef = useRef<ImageAppliedCallback | null>(null);

  // Callback ref for saving images directly (bypasses state)
  const saveImageDirectCallbackRef = useRef<SaveImageDirectCallback | null>(null);

  // Register action callback
  const registerActionCallback = useCallback((callback: ActionCallback) => {
    console.log('[useImageCallbacks] registerActionCallback called');
    actionCallbackRef.current = callback;
  }, []);

  // Unregister action callback
  const unregisterActionCallback = useCallback(() => {
    console.log('[useImageCallbacks] unregisterActionCallback called');
    actionCallbackRef.current = null;
  }, []);

  // Register callback for direct tool use image applied
  const registerImageAppliedCallback = useCallback((callback: ImageAppliedCallback) => {
    console.log('[useImageCallbacks] registerImageAppliedCallback called');
    imageAppliedCallbackRef.current = callback;
  }, []);

  // Unregister image applied callback
  const unregisterImageAppliedCallback = useCallback(() => {
    console.log('[useImageCallbacks] unregisterImageAppliedCallback called');
    imageAppliedCallbackRef.current = null;
  }, []);

  // Notify when image is applied via direct tool use
  const notifyImageApplied = useCallback((imageKey: string, imageUrl: string, previousUrl: string | null) => {
    console.log('[useImageCallbacks] notifyImageApplied called', { imageKey, imageUrl, previousUrl });
    if (imageAppliedCallbackRef.current) {
      imageAppliedCallbackRef.current(imageKey, imageUrl, previousUrl);
    } else {
      console.warn('[useImageCallbacks] notifyImageApplied called but no callback registered');
    }
  }, []);

  // Invoke action callback if registered
  const invokeActionCallback = useCallback((rec: SolomonRecommendation, result: ImageGenerationResult) => {
    console.log('[useImageCallbacks] invokeActionCallback called', {
      recommendationId: rec.id,
      hasCallback: !!actionCallbackRef.current,
    });
    if (actionCallbackRef.current) {
      actionCallbackRef.current(rec, result);
    } else {
      console.warn('[useImageCallbacks] invokeActionCallback called but no callback registered');
    }
  }, []);

  // Register callback for saving images directly
  const registerSaveImageDirectCallback = useCallback((callback: SaveImageDirectCallback) => {
    console.log('[useImageCallbacks] registerSaveImageDirectCallback called');
    saveImageDirectCallbackRef.current = callback;
  }, []);

  // Unregister save image direct callback
  const unregisterSaveImageDirectCallback = useCallback(() => {
    console.log('[useImageCallbacks] unregisterSaveImageDirectCallback called');
    saveImageDirectCallbackRef.current = null;
  }, []);

  // Save image directly (bypasses state, takes imageKey and imageUrl)
  const saveImageDirect = useCallback(async (imageKey: string, imageUrl: string): Promise<boolean> => {
    console.log('[useImageCallbacks] saveImageDirect called', { imageKey, imageUrl });
    if (saveImageDirectCallbackRef.current) {
      // eslint-disable-next-line no-return-await
      return await saveImageDirectCallbackRef.current(imageKey, imageUrl);
    }
    console.warn('[useImageCallbacks] saveImageDirect called but no callback registered');
    return false;
  }, []);

  return {
    actionCallbackRef,
    imageAppliedCallbackRef,
    saveImageDirectCallbackRef,
    registerActionCallback,
    unregisterActionCallback,
    registerImageAppliedCallback,
    unregisterImageAppliedCallback,
    notifyImageApplied,
    invokeActionCallback,
    registerSaveImageDirectCallback,
    unregisterSaveImageDirectCallback,
    saveImageDirect,
  };
}

export default useImageCallbacks;
